import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { clearPendingInvitationToken } from '../utils/pendingInvitationToken';

// User state
interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  /** REV-798: preferred language (a @contract/locales code), or null for none. */
  locale?: string | null;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        isAuthenticated: false,
        isLoading: true,
        setUser: (user) =>
          set({
            user,
            isAuthenticated: !!user,
            isLoading: false,
          }),
        setLoading: (isLoading) => set({ isLoading }),
        logout: () => {
          useActiveOrgStore.getState().clearActiveOrg();
          // REV-878: an invitation token parked for a sign-in round trip is
          // this session's, not the next one's.
          clearPendingInvitationToken();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        },
      }),
      {
        name: 'auth-storage',
        version: 1,
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
        migrate: (persistedState: unknown, version: number) => {
          const state = persistedState as { user?: User | null; isAuthenticated?: boolean };
          // Migration from version 0 (no version) to version 1
          if (version === 0) {
            // Set isAuthenticated based on user presence for old stored data
            return {
              ...state,
              isAuthenticated: !!state.user,
            };
          }
          return state as { user: User | null; isAuthenticated: boolean };
        },
        onRehydrateStorage: () => (state) => {
          // After rehydration, set isLoading to false
          if (state) {
            state.setLoading(false);
          }
        },
      }
    ),
    { name: 'AuthStore' }
  )
);

// Active organization state (REV-211 — lets multi-org members persist a
// "currently selected" org across navigation and sessions).
export interface ActiveOrgSummary {
  id: string;
  slug: string;
  name: string;
  role?: string;
  /** REV-798: the organization's preferred language, or null for none. */
  locale?: string | null;
}

interface ActiveOrgState {
  activeOrg: ActiveOrgSummary | null;
  setActiveOrg: (org: ActiveOrgSummary | null) => void;
  clearActiveOrg: () => void;
}

export const useActiveOrgStore = create<ActiveOrgState>()(
  devtools(
    persist(
      (set) => ({
        activeOrg: null,
        setActiveOrg: (activeOrg) => set({ activeOrg }),
        clearActiveOrg: () => set({ activeOrg: null }),
      }),
      {
        name: 'active-org-storage',
        version: 1,
      }
    ),
    { name: 'ActiveOrgStore' }
  )
);

// UI state
interface UIState {
  /**
   * The DESKTOP panel, which pushes content aside. Persisted, because a user
   * who collapses it means it next time too.
   */
  sidebarOpen: boolean;
  /**
   * REV-724: the NARROW-VIEWPORT overlay, which covers content instead of
   * pushing it. Deliberately separate state, and deliberately NOT persisted.
   *
   * These were one flag. `sidebarOpen` defaults to true and persists, so the
   * same `true` that correctly means "the desktop panel is showing" arrived
   * below 900px meaning "a full-screen modal is up" — which is what the
   * walkthrough saw: the navigation appearing to become a different menu and
   * covering the whole UI, with no user action.
   *
   * An overlay is something you ask for. This starts closed, every time.
   */
  mobileNavOpen: boolean;
  darkMode: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleMobileNav: () => void;
  setMobileNavOpen: (open: boolean) => void;
  toggleDarkMode: () => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        sidebarOpen: true,
        mobileNavOpen: false,
        darkMode: false,
        toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
        setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
        toggleMobileNav: () => set((state) => ({ mobileNavOpen: !state.mobileNavOpen })),
        setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),
        toggleDarkMode: () => set((state) => ({ darkMode: !state.darkMode })),
      }),
      {
        name: 'ui-storage',
        // REV-724: mobileNavOpen is excluded on purpose. Persisting it would
        // reintroduce the bug one reload later.
        partialize: (state) => ({ sidebarOpen: state.sidebarOpen, darkMode: state.darkMode }),
      }
    ),
    { name: 'UIStore' }
  )
);
